import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, FileText, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import Navigation from '../layout/Navigation';

const InvoiceLookup = () => {
  const [formData, setFormData] = useState({
    invoiceId: '',
    invoiceNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.trim() // Trim whitespace to prevent lookup errors
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInvoice(null);

    try {
      const response = await api.get(`/api/invoices/lookup/${formData.invoiceId}/${formData.invoiceNumber}`);
      
      console.log('🔍 Lookup response:', response.data);
      
      if (response.data.success) {
        setInvoice(response.data.invoice);  // Backend returns 'invoice' field
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to lookup invoice';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = () => {
    if (invoice) {
      // Navigate to guest invoice page using the invoice ID
      navigate(`/invoice/${invoice.id}`);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#1F11ED]">
      <Navigation />
      <Helmet>
        <title>Invoice Lookup | Receevit®</title>
        <meta name="description" content="Lookup an invoice securely on Receevit® using your invoice ID and invoice number." />
        <link rel="canonical" href="https://www.receevit.com/lookup" />
        <meta property="og:title" content="Invoice Lookup | Receevit®" />
        <meta property="og:description" content="Lookup an invoice securely on Receevit® using your invoice ID and invoice number." />
        <meta property="og:url" content="https://www.receevit.com/lookup" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-[#1F11ED]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Invoice Lookup
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Enter your Invoice ID and Invoice Number to view your invoice
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lookup Form Section */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Lookup Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 mb-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="invoiceId" className="block text-sm font-medium text-white mb-2">
                  Invoice ID
                </label>
                <input
                  type="text"
                  id="invoiceId"
                  name="invoiceId"
                  value={formData.invoiceId}
                  onChange={handleInputChange}
                  placeholder="e.g., A3B9C2D4"
                  className="w-full px-4 py-3 border border-white/20 bg-white/10 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                  required
                />
                <p className="text-xs text-blue-200 mt-1">
                  8-character lookup code from your invoice email
                </p>
              </div>

              <div>
                <label htmlFor="invoiceNumber" className="block text-sm font-medium text-white mb-2">
                  Invoice Number
                </label>
                <input
                  type="text"
                  id="invoiceNumber"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., INV-20250904-1234"
                  className="w-full px-4 py-3 border border-white/20 bg-white/10 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                  required
                />
                <p className="text-xs text-blue-200 mt-1">
                  Full invoice number from your invoice email
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.invoiceId || !formData.invoiceNumber}
                className="w-full bg-white text-[#1F11ED] py-3 px-6 rounded-xl font-semibold hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1F11ED] mr-2"></div>
                    Looking up invoice...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Lookup Invoice
                  </>
                )}
              </button>
            </form>

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-300 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-red-200">Error</h3>
                    <p className="text-sm text-red-100 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Invoice Preview */}
          {invoice && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 mt-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-white">Invoice Found</h2>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>

              <div className="space-y-6">
                {/* Invoice Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="font-medium text-blue-200">Invoice ID:</span>
                    <p className="text-white text-lg">{invoice.invoiceId}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-200">Invoice Number:</span>
                    <p className="text-white text-lg">{invoice.invoiceNumber}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-200">From:</span>
                    <p className="text-white text-lg">
                      {invoice.sender?.companyName || `${invoice.sender?.firstName} ${invoice.sender?.lastName}`}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-200">To:</span>
                    <p className="text-white text-lg">
                      {invoice.guestRecipient?.companyName || invoice.guestRecipient?.email}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-200">Due Date:</span>
                    <p className="text-white text-lg">{formatDate(invoice.dueDate)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-200">Total:</span>
                    <p className="text-white text-xl font-semibold">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center space-x-3">
                  <span className="font-medium text-blue-200 text-lg">Status:</span>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    invoice.status === 'paid' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                    invoice.status === 'approved' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                    invoice.status === 'pending_approval' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                    'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                    {invoice.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {/* Action Button */}
                <div className="pt-6 border-t border-white/20">
                  <button
                    onClick={handleViewInvoice}
                    className="w-full bg-green-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/50 flex items-center justify-center transition-all duration-300"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    View & Pay Invoice
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          >
            <h3 className="text-xl font-medium text-white mb-4">Need Help?</h3>
            <div className="text-blue-100 space-y-3">
              <p>
                <strong className="text-white">Invoice ID:</strong> A short code (like INV-7K2) found in your invoice email subject line or at the top of the invoice.
              </p>
              <p>
                <strong className="text-white">Invoice Number:</strong> The full invoice number (like INV-20250904-1234) found in your invoice email.
              </p>
              <p>
                If you can't find these details, please contact the sender or check your email for the invoice.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InvoiceLookup;
